<template>
  <div class="sticky-header-wrapper">
    <div class="header-content">
      <v-layout wrap class="px-4 px-sm-6 px-md-12 px-lg-14 py-2">
        <v-flex xs12 class="white--black elevation-6 d-flex align-center header-bar">
          <v-layout wrap px-4 justify-space-between align-center>
            <v-flex @click="$router.push('/')" xs6 sm3 md2 class="d-flex align-center">
              <v-img
                src="@/assets/icons/OhYesMainLogo.png"
                alt="Logo"
                contain
                height="45"
                class="logo-img"
              ></v-img>
            </v-flex>
            <v-flex class="d-none d-lg-flex align-center justify-end header-nav">
              <span class="nav-link" @click="scrollToHero">
                <v-icon color="#f46523" right size="20">mdi-home</v-icon>
                &nbsp;Home
              </span>
              <span class="nav-link" @click="scrollToPackages">
                <v-icon color="#f46523" right size="18">mdi-package-variant</v-icon>
                &nbsp;Packages
              </span>
              <span class="nav-link" @click="goToAbout">
                <v-icon color="#f46523" right size="18">mdi-information</v-icon>
                &nbsp;About
              </span>
              <span class="nav-link" @click="scrollToFooter">
                <v-icon color="#f46523" right size="18">mdi-phone</v-icon>
                &nbsp;Contact Us
              </span>
            </v-flex>
            <v-flex xs6 class="d-flex justify-end d-lg-none">
              <v-btn icon @click="mobileMenuOpen = !mobileMenuOpen" class="mobile-menu-btn">
                <v-icon size="28" color="#f46523">
                  {{ mobileMenuOpen ? 'mdi-close' : 'mdi-menu' }}
                </v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>

      <!-- Mobile Menu -->
      <v-overlay v-model="mobileMenuOpen" class="d-lg-none mobile-overlay">
        <div class="mobile-menu">
          <div class="mobile-menu-header">
            <v-btn icon @click="mobileMenuOpen = false" class="mobile-close-btn">
              <v-icon size="32" color="#f46523">mdi-close</v-icon>
            </v-btn>
          </div>
          <div class="mobile-menu-content">
            <div class="mobile-nav-link" @click="scrollToHeroMobile">
              <v-icon left size="24" color="#f46523">mdi-home</v-icon>
              <span>Home</span>
            </div>
            <div class="mobile-nav-link" @click="scrollToPackagesMobile">
              <v-icon left size="24" color="#f46523">mdi-package-variant</v-icon>
              <span>Packages</span>
            </div>
            <div class="mobile-nav-link" @click="goToAbout">
              <v-icon left size="24" color="#f46523">mdi-information</v-icon>
              <span>About</span>
            </div>
            <div class="mobile-nav-link" @click="scrollToFooterMobile">
              <v-icon left size="24" color="#f46523">mdi-phone</v-icon>
              <span>Contact Us</span>
            </div>
          </div>
        </div>
      </v-overlay>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppHeader',
  data() {
    return {
      mobileMenuOpen: false,
    };
  },
  methods: {
    async scrollToHero() {
      if (this.$route.name !== 'Home') {
        await this.$router.push('/');
        await this.$nextTick();
        setTimeout(() => {
          this.$emit('scroll-to-hero');
        }, 100);
      } else {
        this.$emit('scroll-to-hero');
      }
    },

    async scrollToPackages() {
      if (this.$route.name !== 'Home') {
        await this.$router.push('/');
        await this.$nextTick();
        setTimeout(() => {
          this.$emit('scroll-to-packages');
        }, 100);
      } else {
        this.$emit('scroll-to-packages');
      }
    },

    async scrollToFooter() {
      if (this.$route.name !== 'Home') {
        // Navigate to home first
        await this.$router.push('/');

        // Wait for route to complete
        await this.$nextTick();

        // Wait for Home component to mount
        const waitForHome = new Promise((resolve) => {
          const onMounted = () => {
            this.$root.$off('home-mounted', onMounted);
            resolve();
          };
          this.$root.$on('home-mounted', onMounted);
          
          // Timeout fallback
          setTimeout(resolve, 1000);
        });

        await waitForHome;

        // Wait for images and content to load
        await this.waitForImagesLoad();

        // Additional delay for stability
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Now trigger the scroll
        this.$emit('scroll-to-footer');
      } else {
        // Already on Home page
        await this.waitForImagesLoad();
        await new Promise((resolve) => setTimeout(resolve, 100));
        this.$emit('scroll-to-footer');
      }
    },

    // Helper method to wait for images to load
    waitForImagesLoad() {
      return new Promise((resolve) => {
        const images = document.querySelectorAll('img');
        if (images.length === 0) {
          resolve();
          return;
        }

        let loadedCount = 0;
        const totalImages = images.length;

        const checkAllLoaded = () => {
          loadedCount++;
          if (loadedCount >= totalImages) {
            resolve();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkAllLoaded();
          } else {
            img.addEventListener('load', checkAllLoaded);
            img.addEventListener('error', checkAllLoaded);
          }
        });

        // Timeout after 2 seconds
        setTimeout(resolve, 2000);
      });
    },

    async scrollToHeroMobile() {
      this.mobileMenuOpen = false;
      await this.scrollToHero();
    },

    async scrollToPackagesMobile() {
      this.mobileMenuOpen = false;
      await this.scrollToPackages();
    },

    async scrollToFooterMobile() {
      this.mobileMenuOpen = false;
      await this.scrollToFooter();
    },

    goToAbout() {
      this.$router.push('/AboutUs');
      this.mobileMenuOpen = false;
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000&display=swap');

.header-nav {
  gap: 30px;
}

.nav-link {
  font-family: 'Nunito Sans', sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #f46523;
  cursor: pointer;
  transition: transform 0.3s ease, color 0.3s ease;
  display: flex;
  align-items: center;
  gap: 1px;
}

.nav-link:hover {
  transform: translateY(-2px);
  color: #f46523;
}

.mobile-menu-btn {
  background-color: rgba(244, 101, 35, 0.1) !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.mobile-menu-btn:hover {
  background-color: rgba(244, 101, 35, 0.2) !important;
  transform: scale(1.1);
}

.mobile-overlay {
  z-index: 2000;
}

.mobile-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: white;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.mobile-menu-header {
  display: flex;
  justify-content: flex-end;
  padding: 20px 20px 0 20px;
  align-items: center;
  height: 80px;
}

.mobile-close-btn {
  background-color: rgba(244, 101, 35, 0.1) !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.mobile-close-btn:hover {
  background-color: rgba(244, 101, 35, 0.2) !important;
  transform: scale(1.1);
}

.mobile-menu-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 25px;
  align-items: flex-start;
  padding: 40px 30px;
  animation: slideInUp 0.4s ease-out;
}

.mobile-nav-link {
  display: flex;
  align-items: center;
  gap: 15px;
  font-family: 'Nunito Sans', sans-serif;
  font-weight: 600;
  font-size: 20px;
  color: #333;
  cursor: pointer;
  padding: 18px 25px;
  border-radius: 15px;
  background-color: rgba(244, 101, 35, 0.05);
  border: 1px solid rgba(244, 101, 35, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  justify-content: flex-start;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.mobile-nav-link:hover {
  transform: translateX(8px);
  background-color: rgba(244, 101, 35, 0.1);
  box-shadow: 0 4px 16px rgba(244, 101, 35, 0.15);
  border-color: rgba(244, 101, 35, 0.2);
}

.mobile-nav-link:active {
  transform: translateX(4px) scale(0.98);
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.sticky-header-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background: transparent;
}

.header-content {
  width: 100%;
  padding-top: 15px;
  padding-bottom: 15px;
}

.header-bar {
  background-color: white !important;
  border-radius: 12px;
  height: 90px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.header-bar:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 40px rgba(244, 101, 35, 0.2) !important;
}

.v-image {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.v-image:hover {
  transform: scale(1.05) rotate(3deg);
}

@media (max-width: 1024px) {
  .header-bar {
    height: 80px;
  }
  .logo-img {
    height: 40px !important;
  }
}

@media (max-width: 768px) {
  .header-bar {
    height: 70px;
  }
  .logo-img {
    height: 35px !important;
  }
}

@media (max-width: 480px) {
  .header-bar {
    height: 60px;
    border-radius: 10px;
  }
  .logo-img {
    height: 30px !important;
  }
  .mobile-menu-content {
    padding: 30px 20px;
    gap: 20px;
  }
  .mobile-nav-link {
    font-size: 18px;
    padding: 15px 20px;
  }
}
</style>